/**
 * MultiLocationTransferBackMockup.tsx
 *
 * THROWAWAY front-end-only mockup (no backend, no DB, fake in-memory data) for the
 * proposed "Multiple Location Transfer Back" + "One Input Id Per Day" workflow,
 * shown inside a faithful reproduction of the real "Add New Production Session" modal.
 *
 * Inputs (NEW): the whole production day rolls up into ONE input id (e.g. 900600).
 *   - The day input id is minted once, when the day's first session is created, and
 *     reused by every later session that day.
 *   - Sessions/sub-sessions no longer mint whole numbers — they are suffixes:
 *       Session 900600-1  → subs 900600-1.1, 900600-1.2
 *       Session 900600-2  → subs 900600-2.1, 900600-2.2   (hierarchical day.session.sub)
 *
 * Outputs: one output id per destination per day, contiguous after the input
 *   (900601, 900602…). A second session targeting a destination already used today
 *   REUSES its output id. Removing a destination leaves a TOMBSTONE shipped at 0.
 *
 * Per-day number line: 900600 (IN) → 900601 (OUT) → 900602 (OUT) → next day 900603.
 *
 * Reachable at /dev/multi-location-transfer-back (standalone, no auth/layout).
 * Strings/data are hardcoded on purpose — disposable POC, not production UI.
 */
import React, { useMemo, useState } from 'react';
import {
  X, Plus, Trash2, Check, Package, Scale, MapPin, ArrowRight, RotateCcw,
  Users, Layers, Gauge, Calendar, AlertTriangle, ArrowUpDown,
} from 'lucide-react';

// ---- Fake reference data ---------------------------------------------------
const LOCATIONS = [
  { code: 'M', desc: 'Maspeth' },
  { code: 'X', desc: 'Glendale Sales' },
  { code: 'G', desc: 'Greenpoint' },
  { code: 'C', desc: 'Chinatown' },
];

// Mirrors the example in the real-modal screenshot (Beef Eye Round Cut)
const PRODUCT = {
  name: 'B HEYRC h50 - Beef Eye Round Cut / 切去油老牛',
  packing: '50×1lb',
  unitWeight: 50, // lbs per case
  rawItem: 'B HEYR crf',
  rawName: 'Denuded Eye Round / 去油老牛 19100',
  yield: 94.0, // %
  rawUnitWeight: 71.0,
  meanProd: 117.86,
  byProduct: 'B HTRF hte - Beef Fat Triming / 老牛肥油',
};

const DAY_LABEL = 'Tuesday — 06/30/2026';
const SEQ_START = 900600; // first 9XXXXX number allocated for this mock day

// ---- Types -----------------------------------------------------------------
type DestRow = { id: number; code: string; cases: string };
type SessionRecord = {
  ordinal: number;       // 1, 2, … → session id is `${dayInput}-${ordinal}`
  subCount: number;      // number of sub-sessions (stations)
  targetCases: number;
  splits: { code: string; cases: number; outputNumber: number }[];
};
type OutputRecord = { number: number; code: string; tombstone: boolean };

const locLabel = (code: string) => {
  const l = LOCATIONS.find(x => x.code === code);
  return l ? `${l.code} - ${l.desc}` : code;
};

// ===========================================================================
// The mockup page (Production Guide › Plan with a live day ledger)
// ===========================================================================
const MultiLocationTransferBackMockup: React.FC = () => {
  const [dayInput, setDayInput] = useState<number | null>(null); // single input id for the day
  const [sessions, setSessions] = useState<SessionRecord[]>([]);
  const [outputs, setOutputs] = useState<OutputRecord[]>([]);
  const [nextNumber, setNextNumber] = useState<number>(SEQ_START); // next free whole number
  const [showModal, setShowModal] = useState(false);

  const activeOutputByCode = useMemo(() => {
    const map: Record<string, number> = {};
    outputs.filter(o => !o.tombstone).forEach(o => { map[o.code] = o.number; });
    return map;
  }, [outputs]);

  // Whole-number line for the day = the single input + the outputs (subs are suffixes, not here)
  const allocationOrder = useMemo(() => {
    const rows: { number: number; kind: 'input' | 'output'; label: string; tombstone?: boolean }[] = [];
    if (dayInput != null) rows.push({ number: dayInput, kind: 'input', label: 'Day input' });
    outputs.forEach(o => rows.push({ number: o.number, kind: 'output', label: locLabel(o.code), tombstone: o.tombstone }));
    return rows.sort((a, b) => a.number - b.number);
  }, [dayInput, outputs]);

  const handleCreate = (targetCases: number, subCount: number, rows: DestRow[]) => {
    let counter = nextNumber;
    let di = dayInput;
    if (di == null) di = counter++;            // first session mints the day input id

    const newOutputs: OutputRecord[] = [];
    const numberByCode: Record<string, number> = { ...activeOutputByCode };
    const splits = rows.map(r => {
      let outputNumber = numberByCode[r.code];
      if (outputNumber == null) {
        outputNumber = counter++;              // new destination → next contiguous output id
        numberByCode[r.code] = outputNumber;
        newOutputs.push({ number: outputNumber, code: r.code, tombstone: false });
      }
      return { code: r.code, cases: parseInt(r.cases) || 0, outputNumber };
    });

    setDayInput(di);
    setSessions(prev => [...prev, { ordinal: prev.length + 1, subCount, targetCases, splits }]);
    setOutputs(prev => [...prev, ...newOutputs]);
    setNextNumber(counter);
    setShowModal(false);
  };

  const tombstoneOutput = (number: number) =>
    setOutputs(prev => prev.map(o => (o.number === number ? { ...o, tombstone: true } : o)));

  const resetDay = () => { setDayInput(null); setSessions([]); setOutputs([]); setNextNumber(SEQ_START); };

  const casesToOutput = (number: number) =>
    sessions.reduce((sum, s) => sum + s.splits.filter(sp => sp.outputNumber === number).reduce((a, sp) => a + sp.cases, 0), 0);

  return (
    <div className="min-h-screen bg-gray-900 text-gray-100 p-6">
      <div className="max-w-6xl mx-auto">
        <div className="mb-2 text-sm text-gray-400">Production Guide › Plan</div>
        <h1 className="text-2xl font-bold mb-3">Multi-Location Transfer Back <span className="text-indigo-400">(Mockup)</span></h1>
        <div className="mb-5 rounded-lg border border-yellow-700/50 bg-yellow-900/20 px-3 py-2 text-xs text-yellow-200">
          ⚠️ Throwaway UI mockup — fake data, no backend, nothing saved. One input id per day; sessions/sub-sessions are suffixes; one output id per destination.
        </div>

        <div className="flex items-center justify-between mb-4">
          <div className="text-lg font-semibold">{DAY_LABEL}</div>
          <div className="flex gap-2">
            <button onClick={resetDay} className="flex items-center gap-2 px-3 py-2 text-sm rounded-lg bg-gray-800 border border-gray-700 hover:bg-gray-700">
              <RotateCcw className="w-4 h-4" /> Reset day
            </button>
            <button onClick={() => setShowModal(true)} className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700">
              <Plus className="w-4 h-4" /> Create Session
            </button>
          </div>
        </div>

        {/* Allocation order — proves the tight per-day number line */}
        <div className="mb-6 rounded-xl border border-gray-700 bg-gray-800/60 p-4">
          <div className="text-sm font-semibold mb-3 text-gray-300">Whole-number line (this day) — 1 input + N outputs</div>
          {allocationOrder.length === 0 ? (
            <div className="text-sm text-gray-500">No numbers allocated yet. Create a session to begin.</div>
          ) : (
            <div className="flex flex-wrap items-center gap-2">
              {allocationOrder.map((r, i) => (
                <React.Fragment key={r.number}>
                  {i > 0 && <ArrowRight className="w-3 h-3 text-gray-600" />}
                  <div className={`px-2.5 py-1.5 rounded-lg border text-xs font-mono ${
                    r.tombstone ? 'border-gray-700 bg-gray-800 text-gray-500 line-through'
                      : r.kind === 'input' ? 'border-sky-700 bg-sky-900/30 text-sky-200'
                        : 'border-emerald-700 bg-emerald-900/30 text-emerald-200'
                  }`}>
                    <span className="font-bold">{r.number}</span>{' '}
                    <span className="opacity-70">{r.kind === 'input' ? 'IN' : 'OUT'}</span>{' '}
                    <span className="opacity-80">· {r.label}</span>
                    {r.tombstone && <span className="ml-1 not-italic">@0</span>}
                  </div>
                </React.Fragment>
              ))}
              {dayInput != null && <span className="text-xs text-gray-500 ml-1">· next day starts at {nextNumber}</span>}
            </div>
          )}
          <div className="mt-3 flex gap-4 text-[11px] text-gray-500">
            <span><span className="inline-block w-2.5 h-2.5 rounded-sm bg-sky-700 mr-1 align-middle" />Input (one per day)</span>
            <span><span className="inline-block w-2.5 h-2.5 rounded-sm bg-emerald-700 mr-1 align-middle" />Output (transfer back)</span>
            <span><span className="inline-block w-2.5 h-2.5 rounded-sm bg-gray-700 mr-1 align-middle" />Tombstone — ships at 0</span>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-6">
          {/* Sessions — now grouped under ONE day input id */}
          <div className="rounded-xl border border-gray-700 bg-gray-800/60 p-4">
            <div className="flex items-center justify-between mb-3">
              <div className="text-sm font-semibold text-gray-300">Production day (input)</div>
              {dayInput != null && (
                <div className="text-xs text-gray-400">Day input id <span className="font-mono text-sky-300 font-bold">{dayInput}</span></div>
              )}
            </div>
            {sessions.length === 0 ? <div className="text-sm text-gray-500">No sessions yet.</div> : (
              <div className="space-y-3">
                {sessions.map(s => (
                  <div key={s.ordinal} className="rounded-lg border border-gray-700 bg-gray-900/50 p-3">
                    <div className="flex items-center justify-between">
                      <span className="font-mono text-sky-300 font-bold">{dayInput}-{s.ordinal}</span>
                      <span className="text-xs text-gray-400">{s.targetCases} cases · {s.subCount} sub-sessions</span>
                    </div>
                    <div className="text-sm text-gray-200 mt-0.5">{PRODUCT.name}</div>
                    {/* Sub-sessions (hierarchical day.session.sub) */}
                    <div className="mt-2 flex flex-wrap gap-1.5">
                      {Array.from({ length: s.subCount }, (_, i) => (
                        <span key={i} className="px-1.5 py-0.5 rounded bg-sky-900/30 border border-sky-800/60 text-[11px] font-mono text-sky-200">
                          {dayInput}-{s.ordinal}.{i + 1}
                        </span>
                      ))}
                    </div>
                    {/* Destination splits */}
                    <div className="mt-2 space-y-1 pt-2 border-t border-gray-700">
                      {s.splits.map((sp, i) => (
                        <div key={i} className="flex items-center gap-2 text-xs text-gray-300">
                          <MapPin className="w-3 h-3 text-gray-500" />
                          <span>{locLabel(sp.code)}</span><span className="text-gray-500">·</span><span>{sp.cases} cases</span>
                          <ArrowRight className="w-3 h-3 text-gray-600" /><span className="font-mono text-emerald-300">out {sp.outputNumber}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Outputs */}
          <div className="rounded-xl border border-gray-700 bg-gray-800/60 p-4">
            <div className="text-sm font-semibold mb-3 text-gray-300">Transfer-back outputs</div>
            {outputs.length === 0 ? <div className="text-sm text-gray-500">None yet.</div> : (
              <div className="space-y-3">
                {outputs.slice().sort((a, b) => a.number - b.number).map(o => {
                  const feeders = sessions.filter(s => s.splits.some(sp => sp.outputNumber === o.number)).map(s => `${dayInput}-${s.ordinal}`);
                  const totalCases = o.tombstone ? 0 : casesToOutput(o.number);
                  return (
                    <div key={o.number} className={`rounded-lg border p-3 ${o.tombstone ? 'border-gray-700 bg-gray-900/40' : 'border-emerald-800/60 bg-emerald-900/10'}`}>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <span className={`font-mono font-bold ${o.tombstone ? 'text-gray-500 line-through' : 'text-emerald-300'}`}>{o.number}</span>
                          <span className="text-sm text-gray-200">{locLabel(o.code)}</span>
                          {o.tombstone && <span className="text-[10px] px-1.5 py-0.5 rounded bg-gray-700 text-gray-300">TOMBSTONE · ships @ 0</span>}
                        </div>
                        {!o.tombstone && (
                          <button onClick={() => tombstoneOutput(o.number)} className="flex items-center gap-1 text-xs text-gray-400 hover:text-red-300">
                            <Trash2 className="w-3.5 h-3.5" /> Remove
                          </button>
                        )}
                      </div>
                      <div className="mt-1 text-xs text-gray-400">{totalCases} cases · fed by {feeders.length ? feeders.join(', ') : '—'}</div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>

      {showModal && (
        <CreateSessionMockModal
          dayInput={dayInput}
          sessionOrdinal={sessions.length + 1}
          activeOutputByCode={activeOutputByCode}
          nextNumber={nextNumber}
          onCancel={() => setShowModal(false)}
          onCreate={handleCreate}
        />
      )}
    </div>
  );
};

// ===========================================================================
// Faithful reproduction of the real "Add New Production Session" two-column modal
// ===========================================================================
const CreateSessionMockModal: React.FC<{
  dayInput: number | null;
  sessionOrdinal: number;
  activeOutputByCode: Record<string, number>;
  nextNumber: number;
  onCancel: () => void;
  onCreate: (targetCases: number, subCount: number, rows: DestRow[]) => void;
}> = ({ dayInput, sessionOrdinal, activeOutputByCode, nextNumber, onCancel, onCreate }) => {
  const [targetCases, setTargetCases] = useState('3');
  const [subCount, setSubCount] = useState('2');
  const [sourceLocation, setSourceLocation] = useState('');
  const [rows, setRows] = useState<DestRow[]>([{ id: 1, code: 'M', cases: '2' }, { id: 2, code: 'X', cases: '1' }]);

  const target = parseInt(targetCases) || 0;
  const subs = Math.max(1, parseInt(subCount) || 1);
  const totalWeight = target * PRODUCT.unitWeight;
  const estInput = target * PRODUCT.rawUnitWeight;
  const estOutput = estInput * (PRODUCT.yield / 100);
  const sumCases = rows.reduce((a, r) => a + (parseInt(r.cases) || 0), 0);

  // Resolved ids for THIS session in the day's number line
  const effectiveDayInput = dayInput ?? nextNumber;          // first session mints nextNumber
  const sessionId = `${effectiveDayInput}-${sessionOrdinal}`;

  // Live preview: which output id each destination row will use
  const preview = useMemo(() => {
    let counter = (dayInput == null) ? nextNumber + 1 : nextNumber; // outputs start after the input
    const assigned: Record<string, { number: number; reused: boolean }> = {};
    return rows.map(r => {
      if (!r.code) return { rowId: r.id, number: null as number | null, reused: false };
      if (activeOutputByCode[r.code] != null) assigned[r.code] = { number: activeOutputByCode[r.code], reused: true };
      else if (assigned[r.code] == null) assigned[r.code] = { number: counter++, reused: false };
      return { rowId: r.id, number: assigned[r.code].number, reused: assigned[r.code].reused };
    });
  }, [rows, activeOutputByCode, nextNumber, dayInput]);

  const addRow = () => setRows(prev => [...prev, { id: Math.max(0, ...prev.map(r => r.id)) + 1, code: '', cases: '0' }]);
  const removeRow = (id: number) => setRows(prev => prev.filter(r => r.id !== id));
  const updateRow = (id: number, patch: Partial<DestRow>) => setRows(prev => prev.map(r => (r.id === id ? { ...r, ...patch } : r)));

  const validRows = rows.filter(r => r.code);
  const dupCodes = validRows.length !== new Set(validRows.map(r => r.code)).size;
  const canSave = validRows.length > 0 && !dupCodes && target > 0;

  const selectCls = 'w-full px-2 py-1.5 text-sm border border-gray-600 rounded-lg bg-gray-700 text-white focus:ring-2 focus:ring-indigo-500';
  const labelCls = 'block text-xs font-medium text-gray-300 mb-1';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
      <div className="bg-gray-800 text-gray-100 rounded-xl shadow-2xl w-full max-w-5xl h-[88vh] flex flex-col p-5">
        {/* Header */}
        <div className="flex items-center justify-between mb-3 flex-shrink-0">
          <div className="flex items-center gap-3">
            <h2 className="text-lg font-bold">Add New Production Session</h2>
            <span className="text-xs font-mono px-2 py-0.5 rounded bg-sky-900/40 border border-sky-800 text-sky-200">
              {sessionId}{dayInput == null && <span className="text-sky-400"> · mints day input {effectiveDayInput}</span>}
            </span>
          </div>
          <button onClick={onCancel} className="p-1 hover:bg-gray-700 rounded-lg"><X className="w-5 h-5 text-gray-400" /></button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-gray-700 mb-3 flex-shrink-0">
          <div className="flex items-center gap-2 px-4 py-2 text-sm font-medium border-b-2 border-indigo-500 text-indigo-300"><Package className="w-4 h-4" /> By Cases</div>
          <div className="flex items-center gap-2 px-4 py-2 text-sm font-medium border-b-2 border-transparent text-gray-500"><Scale className="w-4 h-4" /> By Weight</div>
        </div>

        {/* Two-column body */}
        <div className="flex-1 min-h-0 grid grid-cols-[2fr_3fr] gap-4 overflow-hidden">
          {/* LEFT COLUMN */}
          <div className="space-y-3 overflow-y-auto pr-1">
            {/* Dates */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className={labelCls}>Production Date</label>
                <div className="w-full px-2 py-1.5 text-sm border border-gray-600 rounded-lg bg-gray-700 text-white flex items-center justify-between">
                  <span>Tuesday — 06/30/2026</span><Calendar className="w-4 h-4 text-gray-400" />
                </div>
              </div>
              <div>
                <label className={labelCls}>Transfer Date</label>
                <div className="w-full px-2 py-1.5 text-sm border border-gray-600 rounded-lg bg-gray-700 text-white flex items-center justify-between">
                  <span>Monday — 06/29/2026</span><Calendar className="w-4 h-4 text-gray-400" />
                </div>
              </div>
            </div>

            {/* Manpower (amber = none scheduled) */}
            <div className="rounded-lg p-3 bg-amber-900/20 border border-amber-800">
              <div className="flex items-center gap-2 mb-2">
                <Users className="w-4 h-4 text-gray-400" />
                <span className="text-sm font-medium text-gray-300">Scheduled Manpower</span>
              </div>
              <div className="grid grid-cols-3 gap-3 text-sm">
                {[{ n: 0, label: 'Operators', icon: null }, { n: 0, label: 'Packers', icon: null }, { n: 0, label: 'Stations', icon: <Layers className="w-4 h-4" /> }].map((m) => (
                  <div key={m.label} className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-full flex items-center justify-center bg-gray-700 text-gray-500">
                      {m.icon || <span className="font-bold">{m.n}</span>}
                    </div>
                    <span className="text-sm text-gray-400">{m.label === 'Stations' ? `${m.n} Stations` : m.label}</span>
                  </div>
                ))}
              </div>
              <p className="text-xs text-amber-400 mt-2">Awaiting supervisor to schedule operators</p>
            </div>

            {/* Finished Product */}
            <div>
              <label className={labelCls}>Finished Product</label>
              <div className={`${selectCls} flex items-center justify-between`}>
                <span className="truncate">{PRODUCT.name}</span>
                <X className="w-4 h-4 text-gray-400 shrink-0" />
              </div>
            </div>

            {/* Product Info stats */}
            <div className="bg-gray-700/50 rounded-lg px-3 py-2">
              <div className="grid grid-cols-4 gap-2 text-sm">
                <div>
                  <span className="text-xs text-gray-400">Packing:</span>
                  <p className="font-medium text-white">{PRODUCT.packing}</p>
                </div>
                <div>
                  <span className="text-xs text-gray-400">Unit Weight:</span>
                  <p className="font-medium text-white">{PRODUCT.unitWeight.toFixed(2)} lbs</p>
                </div>
                <div>
                  <span className="text-xs text-gray-400">Target Cases:</span>
                  <input type="number" min={0} value={targetCases} onChange={e => setTargetCases(e.target.value)}
                    className="mt-0.5 w-full px-2 py-1 border border-gray-600 rounded-lg bg-gray-700 text-white focus:ring-2 focus:ring-indigo-500 text-sm font-medium" />
                </div>
                <div>
                  <span className="text-xs text-gray-400">Total Weight:</span>
                  <p className="font-medium text-green-400 underline decoration-dotted">{totalWeight > 0 ? `${totalWeight.toFixed(2)} lbs` : '-'}</p>
                </div>
              </div>
            </div>

            {/* Sub-sessions (NEW) — drives the hierarchical ids */}
            <div className="rounded-lg px-3 py-2 bg-sky-900/10 border border-sky-800/50">
              <div className="flex items-center justify-between">
                <div>
                  <label className="text-xs font-medium text-sky-200">Sub-sessions (stations)</label>
                  <p className="text-[10px] text-gray-500 mt-0.5">Each becomes a work unit under this session.</p>
                </div>
                <input type="number" min={1} value={subCount} onChange={e => setSubCount(e.target.value)}
                  className="w-16 px-2 py-1 text-sm text-center border border-gray-600 rounded-lg bg-gray-700 text-white focus:ring-2 focus:ring-indigo-500" />
              </div>
              <div className="mt-2 flex flex-wrap gap-1.5">
                {Array.from({ length: subs }, (_, i) => (
                  <span key={i} className="px-1.5 py-0.5 rounded bg-sky-900/30 border border-sky-800/60 text-[11px] font-mono text-sky-200">
                    {sessionId}.{i + 1}
                  </span>
                ))}
              </div>
            </div>

            {/* Labor estimator */}
            <div className="rounded-lg px-3 py-2 bg-slate-800/50 border border-slate-700">
              <div className="flex items-center gap-2 mb-2">
                <Gauge className="w-4 h-4 text-slate-400" />
                <span className="text-sm font-medium text-gray-300">Labor Factor</span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <div className="min-w-0">
                  <p className="font-medium text-white truncate">{PRODUCT.rawItem}</p>
                  <p className="text-xs text-gray-400 truncate">{PRODUCT.rawName}</p>
                </div>
                <span className="text-xs font-medium px-1.5 py-0.5 rounded bg-gray-700 text-gray-400 shrink-0">No Labour Factor</span>
              </div>
              <div className="rounded bg-slate-700/50 px-2 py-1.5 mt-2 flex items-center justify-between text-xs">
                <span className="text-gray-400">Estimated Output</span>
                <span className="font-semibold text-orange-400">{estOutput.toFixed(2)} lb <span className="text-[10px] text-gray-500">({Math.floor(estOutput / PRODUCT.unitWeight)} cases · yield only)</span></span>
              </div>
            </div>
          </div>

          {/* RIGHT COLUMN */}
          <div className="overflow-y-auto min-w-0 pr-1 border-l border-gray-700 pl-4 space-y-3">
            {/* Raw material header */}
            <div className="flex items-center justify-between">
              <span className="text-sm font-semibold text-gray-200">Raw Material Item # <span className="text-red-400">*</span></span>
              <div className="flex items-center gap-2 text-xs text-gray-400">
                <ArrowUpDown className="w-3.5 h-3.5" />
                <div className="px-2 py-1 border border-gray-600 rounded bg-gray-700">Preference</div>
              </div>
            </div>

            {/* Raw material row */}
            <div className="rounded-lg border border-gray-700 bg-gray-900/40 overflow-hidden">
              <div className="grid grid-cols-[2fr_1fr_1fr_1fr] gap-2 px-3 py-1.5 text-[11px] text-gray-500 border-b border-gray-700">
                <span>Raw Material Item #</span><span>Yield</span><span>Mean Prod #</span><span>Unit Weight</span>
              </div>
              <div className="grid grid-cols-[2fr_1fr_1fr_1fr] gap-2 px-3 py-2 text-sm items-center">
                <div className="min-w-0"><p className="font-medium text-white">{PRODUCT.rawItem}</p><p className="text-xs text-gray-400 truncate">{PRODUCT.rawName}</p></div>
                <span className="text-white">{PRODUCT.yield.toFixed(2)}%</span>
                <span className="text-white">{PRODUCT.meanProd.toFixed(2)} lb</span>
                <span className="text-white">{PRODUCT.rawUnitWeight.toFixed(2)} lb</span>
              </div>
              <div className="px-3 py-1.5 text-xs bg-indigo-900/30 text-indigo-200 border-t border-gray-700">VB6 Balance: <span className="font-medium">M: 786 CS</span> · <span className="font-medium">X: 49 CS</span></div>
            </div>

            {/* Total available bar */}
            <div>
              <div className="flex items-center justify-between text-xs text-gray-400 mb-1"><span>Total Available (@HTE)</span><span>0.00</span></div>
              <div className="h-1.5 rounded-full bg-gray-700"><div className="h-1.5 rounded-full bg-indigo-500" style={{ width: '4%' }} /></div>
            </div>

            {/* Selected materials */}
            <div>
              <div className="text-[11px] font-semibold text-gray-400 tracking-wide mb-1">SELECTED MATERIALS</div>
              <div className="rounded-lg border border-gray-700 bg-gray-900/40 p-3">
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-[10px] px-1.5 py-0.5 rounded bg-indigo-900/40 text-indigo-300">Primary Product</span>
                  <span className="text-sm font-medium text-white">{PRODUCT.rawItem} - Denuded Eye Round</span>
                  <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-900/30 text-amber-300 flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> Transfer needed</span>
                  <Trash2 className="w-4 h-4 text-red-400 ml-auto" />
                </div>
                <div className="grid grid-cols-6 gap-2 text-xs">
                  {[
                    ['Available', '0.00 lb'], ['Yield', `${PRODUCT.yield.toFixed(2)}%`], ['Unit Weight', `${PRODUCT.rawUnitWeight.toFixed(2)} lb`],
                    ['Est. Cases', String(target)], ['Est. Input', estInput.toFixed(2)], ['Est. Output', estOutput.toFixed(2)],
                  ].map(([k, v]) => (
                    <div key={k}><span className="text-gray-500">{k}</span><p className="font-medium text-white">{v}</p></div>
                  ))}
                </div>
              </div>
              <div className="rounded-lg border border-gray-700 bg-gray-900/40 p-3 mt-2">
                <span className="text-[10px] px-1.5 py-0.5 rounded bg-purple-900/30 text-purple-300">Secondary Product / By-Product</span>
                <p className="text-sm text-white mt-1.5">{PRODUCT.byProduct}</p>
              </div>
            </div>

            {/* Totals */}
            <div className="flex items-center gap-4 text-sm pt-1 border-t border-gray-700">
              <span className="text-gray-400">Total Cases: <span className="text-white font-medium">{target}</span></span>
              <span className="text-gray-400">Total Input Weight: <span className="text-white font-medium">{estInput.toFixed(2)} lbs</span></span>
              <span className="text-gray-400">Total Output Weight: <span className="text-white font-medium">{estOutput.toFixed(2)} lbs</span></span>
            </div>

            {/* ---- Source Location + NEW multi-destination Transfer Back ---- */}
            <div className="pt-3 mt-1 border-t border-gray-700 grid grid-cols-2 gap-4 items-start">
              {/* Source Location (unchanged) */}
              <div>
                <label className={labelCls}>Source Location <span className="text-red-400">*</span></label>
                <select value={sourceLocation} onChange={e => setSourceLocation(e.target.value)} className={selectCls}>
                  <option value="">Select a location…</option>
                  {LOCATIONS.map(l => <option key={l.code} value={l.code}>{l.code} - {l.desc}</option>)}
                </select>
                <p className="mt-0.5 text-xs text-gray-500">An inbound transfer will be auto-created</p>
              </div>

              {/* NEW: Transfer Back Destinations (multi) */}
              <div className="rounded-lg border border-indigo-800/50 bg-indigo-900/10 p-2.5">
                <div className="flex items-center justify-between mb-1.5">
                  <label className="text-xs font-semibold text-indigo-200">Transfer Back Destinations</label>
                  <span className={`text-[11px] ${sumCases === target ? 'text-emerald-300' : 'text-amber-300'}`}>{sumCases} / {target} cases</span>
                </div>
                <div className="space-y-1.5">
                  {rows.map(r => {
                    const p = preview.find(x => x.rowId === r.id);
                    return (
                      <div key={r.id} className="flex items-center gap-1.5">
                        <select value={r.code} onChange={e => updateRow(r.id, { code: e.target.value })} className="flex-1 px-1.5 py-1 text-xs border border-gray-600 rounded bg-gray-700 text-white focus:ring-2 focus:ring-indigo-500">
                          <option value="">Select…</option>
                          {LOCATIONS.map(l => <option key={l.code} value={l.code}>{l.code} - {l.desc}</option>)}
                        </select>
                        <input type="number" min={0} value={r.cases} onChange={e => updateRow(r.id, { cases: e.target.value })} className="w-12 px-1.5 py-1 text-xs border border-gray-600 rounded bg-gray-700 text-white focus:ring-2 focus:ring-indigo-500" />
                        <div className="w-28 text-[11px] font-mono leading-tight">
                          {p?.number != null ? <span className={p.reused ? 'text-sky-300' : 'text-emerald-300'}>→ {p.number} {p.reused ? '(reuse)' : '(new)'}</span> : <span className="text-gray-600">→ —</span>}
                        </div>
                        <button onClick={() => removeRow(r.id)} className="p-1 text-gray-400 hover:text-red-300"><Trash2 className="w-3.5 h-3.5" /></button>
                      </div>
                    );
                  })}
                </div>
                <button onClick={addRow} className="mt-2 flex items-center gap-1 text-[11px] font-medium text-indigo-300 hover:text-indigo-200"><Plus className="w-3 h-3" /> Add destination</button>
                {dupCodes && <div className="mt-1 text-[11px] text-red-300">Each destination only once per session.</div>}
                <p className="mt-1.5 text-[10px] text-gray-500">Each becomes an outbound transfer. Day input <span className="font-mono text-sky-300">{effectiveDayInput}</span> · session <span className="font-mono text-sky-300">{sessionId}</span>. A destination used earlier today reuses its output #.</p>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-3 mt-4 pt-3 border-t border-gray-700 flex-shrink-0">
          <button onClick={onCancel} className="px-4 py-2 text-sm font-medium text-gray-300 bg-gray-700 border border-gray-600 rounded-lg hover:bg-gray-600">Cancel</button>
          <button onClick={() => onCreate(target, subs, validRows)} disabled={!canSave} className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed">
            <Check className="w-4 h-4" /> Save
          </button>
        </div>
      </div>
    </div>
  );
};

export default MultiLocationTransferBackMockup;
